import { EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isValid, parse, parseISO } from 'date-fns';
import { useState } from 'react';
import type { Lottery } from '@/types/Lottery';

const columnHelper = createColumnHelper<Lottery>();

// Phone number formatting utility
function _formatPhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 3) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) - ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) - ${digits.slice(3, 7)} - ${digits.slice(7)}`;
}

export const getColumns = (
  onView: (id: string) => void,
  onDelete: (id: string) => void,
  onUpdateDate: (id: string, field: 'startDate' | 'endDate', value: string) => void
) => [
  columnHelper.accessor('name', {
    header: 'Name',
    size: 200,
    minSize: 150,
  }),
  columnHelper.accessor('startDate', {
    header: 'Open Date',
    size: 200,
    minSize: 180,
    cell: (props) => {
      const [isEditing, setIsEditing] = useState(false);
      const [editValue, setEditValue] = useState(() => {
        try {
          return format(parseISO(props.getValue()), 'MM/dd/yyyy');
        } catch {
          return props.getValue();
        }
      });

      const handleSave = () => {
        try {
          // Convert back to ISO format for the API
          const parsedDate = parse(editValue, 'MM/dd/yyyy', new Date());
          if (!isValid(parsedDate)) {
            throw new Error('Invalid date format');
          }
          const isoString = parsedDate.toISOString();
          onUpdateDate(props.row.original.id, 'startDate', isoString);
          setIsEditing(false);
        } catch (_error) {
          console.error('Invalid date format');
          setEditValue(format(parseISO(props.getValue()), 'MM/dd/yyyy'));
        }
      };

      const handleCancel = () => {
        setEditValue(format(parseISO(props.getValue()), 'MM/dd/yyyy'));
        setIsEditing(false);
      };

      if (isEditing) {
        return (
          <div className='flex flex-col gap-1'>
            <input
              type='text'
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className='px-2 py-1 text-sm border rounded'
              placeholder='MM/DD/YYYY'
            />
            <div className='flex gap-1'>
              <button onClick={handleSave} className='px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600'>
                Save
              </button>
              <button onClick={handleCancel} className='px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600'>
                Cancel
              </button>
            </div>
          </div>
        );
      }

      return (
        <button
          type='button'
          className='cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-left w-full'
          onClick={() => setIsEditing(true)}
        >
          {format(parseISO(props.getValue()), 'MM/dd/yyyy')}
        </button>
      );
    },
  }),
  columnHelper.accessor('endDate', {
    header: 'Closed Date',
    size: 200,
    minSize: 180,
    cell: (props) => {
      const [isEditing, setIsEditing] = useState(false);
      const [editValue, setEditValue] = useState(() => {
        try {
          return format(parseISO(props.getValue()), 'MM/dd/yyyy');
        } catch {
          return props.getValue();
        }
      });

      const handleSave = () => {
        try {
          // Convert back to ISO format for the API
          const parsedDate = parse(editValue, 'MM/dd/yyyy', new Date());
          if (!isValid(parsedDate)) {
            throw new Error('Invalid date format');
          }
          const isoString = parsedDate.toISOString();
          onUpdateDate(props.row.original.id, 'endDate', isoString);
          setIsEditing(false);
        } catch (_error) {
          console.error('Invalid date format');
          setEditValue(format(parseISO(props.getValue()), 'MM/dd/yyyy'));
        }
      };

      const handleCancel = () => {
        setEditValue(format(parseISO(props.getValue()), 'MM/dd/yyyy'));
        setIsEditing(false);
      };

      if (isEditing) {
        return (
          <div className='flex flex-col gap-1'>
            <input
              type='text'
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className='px-2 py-1 text-sm border rounded'
              placeholder='MM/DD/YYYY'
            />
            <div className='flex gap-1'>
              <button onClick={handleSave} className='px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600'>
                Save
              </button>
              <button onClick={handleCancel} className='px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600'>
                Cancel
              </button>
            </div>
          </div>
        );
      }

      return (
        <button
          type='button'
          className='cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-left w-full'
          onClick={() => setIsEditing(true)}
        >
          {format(parseISO(props.getValue()), 'MM/dd/yyyy')}
        </button>
      );
    },
  }),
  columnHelper.accessor('maxParticipants', {
    header: 'Max Participants',
    size: 150,
    minSize: 120,
  }),
  columnHelper.accessor('totalWinners', {
    header: 'Total Winners',
    size: 150,
    minSize: 120,
  }),
  //columnHelper.accessor('contactInfo', {
  //  header: 'Phone Number',
  //  size: 180,
  //  minSize: 150,
  //  cell: (props) => formatPhoneNumber((props.getValue() as string) ?? ''),
  //}),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 100,
    minSize: 80,
    cell: (props) => (
      <div className='flex items-center gap-2'>
        <button onClick={() => onView(props.row.original.id)} className='text-gray-500 hover:text-gray-700'>
          <EyeIcon className='h-5 w-5' />
        </button>
        <button onClick={() => onDelete(props.row.original.id)} className='text-gray-500 hover:text-gray-700'>
          <TrashIcon className='h-5 w-5' />
        </button>
      </div>
    ),
  }),
];
